import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@/components/Icon';

/** Uniwersalne zdarzenie kalendarza (wyjazd brygady, urlop, …). Daty jako 'YYYY-MM-DD'. */
export interface CalendarEvent {
  id: number | string;
  title: string;
  startDate: string;
  endDate: string;
  color?: string | null;
  startTime?: string | null;
  tooltip?: string;
}

/**
 * Kalendarz miesięczny (bez zewnętrznych bibliotek). Komponent KONTROLOWANY — rodzic trzyma
 * bieżący miesiąc i dostarcza wyjazdy nachodzące na widoczne okno. Siatka stała 6×7 = 42 komórki,
 * poniedziałek pierwszy (PL). Wyjazd wielodniowy renderujemy jako kafelek w KAŻDEJ komórce swojego
 * zakresu (proste, odporne na layout). Ciągłe paski (spanning bars) świadomie poza v1.
 *
 * W komórce mieszczą się MAX_CHIPS kafelków; nadmiar chowamy pod przyciskiem „+N więcej", który
 * otwiera okno z PEŁNĄ listą wpisów danego dnia (bez tego nadmiarowe wpisy były nieosiągalne —
 * kliknięcie w komórkę wywołuje onDayClick, czyli np. dodawanie urlopu).
 *
 * Uwaga na strefy czasu: daty trzymamy jako łańcuchy YYYY-MM-DD i porównujemy leksykalnie —
 * NIE używamy new Date('YYYY-MM-DD') (interpretowane jako UTC → przesunięcie o dzień).
 */

const WEEKDAYS = ['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'];
const MAX_CHIPS = 3;

/** Data lokalna → 'YYYY-MM-DD' (bez konwersji stref czasu). */
function ymd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** 'YYYY-MM-DD' → Date w strefie lokalnej (new Date(iso) dałoby UTC → przesunięcie o dzień). */
function parseIso(iso: string): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Druga linia wpisu w oknie dnia: tooltip bez powtórzonego na początku tytułu (i separatora). */
function detailLine(t: CalendarEvent): string {
  const tip = (t.tooltip ?? '').trim();
  if (!tip) return '';
  return tip.startsWith(t.title) ? tip.slice(t.title.length).replace(/^[\s—·–-]+/, '') : tip;
}

/** Odmiana: „1 wpis", „2 wpisy", „5 wpisów" (z wyjątkiem 12–14). */
function wpisy(n: number): string {
  const last = n % 10, last2 = n % 100;
  if (n === 1) return '1 wpis';
  if (last >= 2 && last <= 4 && !(last2 >= 12 && last2 <= 14)) return `${n} wpisy`;
  return `${n} wpisów`;
}

interface Props {
  month: Date;
  events: CalendarEvent[];
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onToday: () => void;
  onDayClick: (dateIso: string) => void;
  onEventClick: (event: CalendarEvent) => void;
}

export function MonthCalendar({ month, events, onPrevMonth, onNextMonth, onToday, onDayClick, onEventClick }: Props) {
  const y = month.getFullYear();
  const m = month.getMonth();
  const todayIso = ymd(new Date());
  const title = new Intl.DateTimeFormat('pl-PL', { month: 'long', year: 'numeric' }).format(new Date(y, m, 1));
  /** Dzień rozwinięty do pełnej listy wpisów ('YYYY-MM-DD') albo null. */
  const [openDay, setOpenDay] = useState<string | null>(null);

  const cells = useMemo(() => {
    const firstOfMonth = new Date(y, m, 1);
    const startWeekday = (firstOfMonth.getDay() + 6) % 7; // poniedziałek = 0
    const out: { date: Date; iso: string; inMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(y, m, 1 - startWeekday + i);
      out.push({ date: d, iso: ymd(d), inMonth: d.getMonth() === m });
    }
    return out;
  }, [y, m]);

  // Stała, sensowna kolejność: najpierw wpisy z godziną (rosnąco), potem całodniowe alfabetycznie —
  // dzięki temu widoczna trójka nie zależy od kolejności zwróconej przez API.
  const sortedEvents = useMemo(() =>
    [...events].sort((a, b) =>
      (a.startTime ?? '99:99').localeCompare(b.startTime ?? '99:99')
      || a.title.localeCompare(b.title, 'pl')),
    [events]);

  const eventsForDay = (iso: string) =>
    sortedEvents.filter(t => t.startDate <= iso && t.endDate >= iso);

  return (
    <div className="card">
      <div className="card-body !p-0">
        {/* Pasek nawigacji */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-ink-100">
          <button onClick={onPrevMonth} className="btn btn-ghost btn-icon" title="Poprzedni miesiąc">
            <Icon name="chevron" className="w-4 h-4 rotate-180"/>
          </button>
          <div className="font-semibold text-ink-900 text-[15px] capitalize min-w-[160px] text-center">{title}</div>
          <button onClick={onNextMonth} className="btn btn-ghost btn-icon" title="Następny miesiąc">
            <Icon name="chevron" className="w-4 h-4"/>
          </button>
          <button onClick={onToday} className="btn btn-secondary btn-sm ml-2">Dziś</button>
        </div>

        {/* Nagłówki dni */}
        <div className="grid grid-cols-7 border-b border-ink-100 bg-ink-50/50">
          {WEEKDAYS.map((w, i) => (
            <div key={w} className={'px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-center '
              + (i >= 5 ? 'text-ink-400' : 'text-ink-500')}>{w}</div>
          ))}
        </div>

        {/* Siatka 6×7 */}
        <div className="grid grid-cols-7">
          {cells.map((cell, idx) => {
            const dayEvents = eventsForDay(cell.iso);
            const isToday = cell.iso === todayIso;
            const isWeekend = idx % 7 >= 5;
            return (
              <div
                key={cell.iso}
                onClick={() => onDayClick(cell.iso)}
                className={'min-h-[92px] border-b border-r border-ink-100 p-1 cursor-pointer transition '
                  + (cell.inMonth ? 'bg-white hover:bg-water-50/40' : 'bg-ink-50/40')
                  + (idx % 7 === 0 ? ' border-l' : '')}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className={'inline-grid place-items-center w-5 h-5 rounded-full text-[11px] '
                    + (isToday ? 'bg-water-500 text-white font-semibold'
                      : cell.inMonth ? (isWeekend ? 'text-ink-400' : 'text-ink-600') : 'text-ink-300')}>
                    {cell.date.getDate()}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5">
                  {dayEvents.slice(0, MAX_CHIPS).map(t => (
                    <button
                      key={t.id}
                      onClick={e => { e.stopPropagation(); onEventClick(t); }}
                      title={t.tooltip ?? t.title}
                      className="text-left text-[10px] leading-tight px-1 py-0.5 rounded text-white truncate hover:opacity-90"
                      style={{ background: t.color || '#6b7280' }}
                    >
                      {t.startTime ? t.startTime.slice(0, 5) + ' ' : ''}{t.title}
                    </button>
                  ))}
                  {dayEvents.length > MAX_CHIPS && (
                    <button
                      onClick={e => { e.stopPropagation(); setOpenDay(cell.iso); }}
                      aria-haspopup="dialog"
                      title={`Pokaż wszystkie wpisy (${wpisy(dayEvents.length)})`}
                      className="text-left text-[10px] leading-tight px-1 py-0.5 rounded text-ink-500 font-medium
                                 hover:bg-ink-100 hover:text-ink-700 transition"
                    >
                      +{dayEvents.length - MAX_CHIPS} więcej
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {openDay && (
        <DayEventsModal
          dateIso={openDay}
          events={eventsForDay(openDay)}
          onClose={() => setOpenDay(null)}
          onEventClick={ev => { setOpenDay(null); onEventClick(ev); }}
        />
      )}
    </div>
  );
}

/** Pełna lista wpisów jednego dnia — zawartość komórki bez limitu MAX_CHIPS. */
function DayEventsModal({ dateIso, events, onClose, onEventClick }: {
  dateIso: string;
  events: CalendarEvent[];
  onClose: () => void;
  onEventClick: (event: CalendarEvent) => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const heading = new Intl.DateTimeFormat('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    .format(parseIso(dateIso));

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={`Wpisy — ${heading}`} onClick={e => e.stopPropagation()}
           className="bg-white rounded-lg shadow-xl w-full max-w-[480px] max-h-[92vh] flex flex-col">
        <div className="px-5 py-3 border-b border-ink-100 flex items-center gap-3">
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-ink-900 first-letter:uppercase truncate">{heading}</h2>
            <div className="text-[11px] text-ink-400">{wpisy(events.length)}</div>
          </div>
          <button onClick={onClose} className="btn btn-ghost btn-icon text-ink-400 ml-auto shrink-0" title="Zamknij">
            <Icon name="x" className="w-3.5 h-3.5"/>
          </button>
        </div>
        <div className="p-3 overflow-auto flex flex-col gap-1">
          {events.map(t => {
            // Tooltipy zwykle zaczynają się od tytułu („Jan Kowalski — Urlop…") — nie powtarzamy go w 2. linii.
            const detail = detailLine(t);
            return (
              <button
                key={t.id}
                onClick={() => onEventClick(t)}
                title={t.tooltip ?? t.title}
                className="text-left flex items-start gap-2 px-2 py-1.5 rounded hover:bg-ink-50 transition"
              >
                <span className="inline-block w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ background: t.color || '#6b7280' }}/>
                <span className="min-w-0">
                  <span className="block text-[13px] text-ink-900 font-medium truncate">
                    {t.startTime ? t.startTime.slice(0, 5) + ' ' : ''}{t.title}
                  </span>
                  {(detail || t.startDate !== t.endDate) && (
                    <span className="block text-[11px] text-ink-400">
                      {t.startDate !== t.endDate && <span className="font-mono mr-1.5">{t.startDate}–{t.endDate}</span>}
                      {detail}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
